import { useMemo } from 'react';
import { SidebarSearchStatic } from './SidebarSearchStatic';

interface SidebarSearchAnimatedProps {
  width?: number;
  height?: number;
  className?: string;
  duration?: number;
  easing?: string;
  color?: string;
  hoverColor?: string;
}

export const SidebarSearchAnimated = ({
  width = 24,
  height = 24,
  className,
  duration = 0.45,
  easing = 'ease-in-out',
  color = 'currentColor',
  hoverColor = color
}: SidebarSearchAnimatedProps) => {
  const pathData = useMemo(() => {
    const magnifierPath =
      'M8.56261 3.5C6.38799 3.5 4.62511 5.26288 4.62511 7.4375C4.62511 8.52502 5.06501 9.50836 5.77838 10.2217C6.49175 10.9351 7.4751 11.375 8.56261 11.375C10.7372 11.375 12.5001 9.61212 12.5001 7.4375C12.5001 5.26288 10.7372 3.5 8.56261 3.5ZM3.12511 7.4375C3.12511 4.43445 5.55957 2 8.56261 2C11.5657 2 14.0001 4.43445 14.0001 7.4375C14.0001 10.4405 11.5657 12.875 8.56261 12.875C7.33185 12.875 6.19579 12.4654 5.28463 11.7761L2.56066 14.5L1.5 13.4393L4.22397 10.7155C3.53468 9.80432 3.12511 8.66826 3.12511 7.4375Z';

    // Use lens center as pivot for rotation
    const lensPivot = {
      cx: 8.56,
      cy: 7.44
    };

    return { magnifierPath, lensPivot };
  }, []);

  const svgEasing = easing === 'ease-in-out' ? 'ease' : easing; // eslint-disable-line @typescript-eslint/no-unused-vars

  const animationAttributes = {
    dur: `${duration * 2}s`,
    begin: 'searchParentGroup.mouseover',
    end: 'searchParentGroup.mouseout',
    repeatCount: 'indefinite',
    calcMode: 'spline',
    keySplines: '0.42 0 0.58 1; 0.42 0 0.58 1',
    keyTimes: '0; 0.5; 1',
    restart: 'whenNotActive' as const
  };

  return (
    <div className="group">
      {/* Static version */}
      <div className={`group-hover:hidden ${className || ''}`} style={{ width, height }}>
        <SidebarSearchStatic width={width} height={height} color={color} className="size-full" />
      </div>

      {/* Animated version */}
      <div className={`hidden group-hover:block ${className || ''}`} style={{ width, height }}>
        <svg
          width={width}
          height={height}
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <g id="searchParentGroup">
            {/* Hover capture */}
            <rect width="16" height="16" fill="transparent" style={{ cursor: 'pointer' }} />

            {/* Expressive wiggle group */}
            <g>
              {/* Lift and shift slightly to the left */}
              <animateTransform
                attributeName="transform"
                type="translate"
                values="0 0; -0.7 -0.6; 0 0"
                {...animationAttributes}
              />

              {/* Tilt around the lens center (more expressive angle) */}
              <animateTransform
                attributeName="transform"
                type="rotate"
                additive="sum"
                values={`0 ${pathData.lensPivot.cx} ${pathData.lensPivot.cy}; -6 ${pathData.lensPivot.cx} ${pathData.lensPivot.cy}; 0 ${pathData.lensPivot.cx} ${pathData.lensPivot.cy}`}
                {...animationAttributes}
              />

              {/* Slight scale punch for extra expressiveness */}
              <animateTransform
                attributeName="transform"
                type="scale"
                additive="sum"
                values="1 1; 1.06 1.06; 1 1"
                {...animationAttributes}
              />

              {/* Magnifier */}
              <path d={pathData.magnifierPath} fill={color}>
                <animate
                  attributeName="fill"
                  values={`${color}; ${hoverColor}; ${color}`}
                  {...animationAttributes}
                />
              </path>
            </g>
          </g>
        </svg>
      </div>
    </div>
  );
};
